import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { currency } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw, AlertTriangle, Clock, Calendar, Check, XCircle, Zap, MessageSquare, Eye } from "lucide-react";
import RenewalNotesModal from "@/components/RenewalNotesModal";
import RenewalDecisionModal from "@/components/RenewalDecisionModal";
import AttorneyDigestPreviewModal from "@/components/AttorneyDigestPreviewModal";

const COLS = [
  { key: "overdue", label: "Overdue",     tone: "red",    icon: AlertTriangle, desc: "Renewal date has passed" },
  { key: "m3",      label: "≤ 3 months",  tone: "amber",  icon: Clock,         desc: "Notice window imminent" },
  { key: "m6",      label: "≤ 6 months",  tone: "amber",  icon: Clock,         desc: "Half-year horizon" },
  { key: "m9",      label: "≤ 9 months",  tone: "blue",   icon: Calendar,      desc: "Plan ahead" },
  { key: "m12",     label: "≤ 12 months", tone: "blue",   icon: Calendar,      desc: "Annual planning" },
];

const TONE = {
  red:   "bg-red-50 border-red-200 text-red-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  blue:  "bg-blue-50 border-blue-200 text-blue-800",
};

export default function RenewalPipeline() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canDecide = ["attorney","general_counsel","legal_manager","it_admin"].includes(user?.role);
  const canDigest = ["legal_manager","general_counsel","it_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [notesFor, setNotesFor] = useState(null);
  const [decideFor, setDecideFor] = useState(null); // { contract, decision }
  const [digestOpen, setDigestOpen] = useState(false);
  const load = () => api.get("/renewals/pipeline").then(r => setData(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const openDecide = (r, decision) => setDecideFor({ contract: r, decision });

  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="p-6" data-testid="renewal-pipeline-page">
      <div className="mb-5 flex items-center gap-3">
        <RefreshCw className="w-5 h-5 text-[#00AE42]" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phase 10 · Renewal Radar</div>
          <h1 className="text-2xl font-bold text-slate-900">Renewal Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Executed contracts bucketed by renewal date. Emails go out 12/9/6/3-month milestones automatically.</p>
        </div>
        <button onClick={load} className="ml-auto h-8 px-3 text-xs border border-slate-200 rounded hover:bg-slate-50" data-testid="renewal-reload">Refresh</button>
        {canDigest && (
          <button onClick={() => setDigestOpen(true)}
                  data-testid="renewal-digest-preview-btn"
                  className="h-8 px-3 text-xs font-semibold rounded border border-[#005289] text-[#005289] hover:bg-slate-50 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Preview digest
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {COLS.map(col => {
          const rows = data[col.key] || [];
          const Icon = col.icon;
          return (
            <div key={col.key} className={`border rounded-md ${TONE[col.tone]}`} data-testid={`bucket-${col.key}`}>
              <div className="px-3 py-2 border-b border-current/20 flex items-center gap-2">
                <Icon className="w-4 h-4"/>
                <div className="font-bold text-sm">{col.label}</div>
                <span className="ml-auto text-xs font-mono">{rows.length}</span>
              </div>
              <div className="p-2 bg-white space-y-2 max-h-[70vh] overflow-y-auto">
                {rows.length === 0 ? <div className="text-[11px] text-slate-400 text-center py-4">Nothing here.</div> : null}
                {rows.map(r => (
                  <div key={r.contract_id} className="border border-slate-200 rounded p-2 hover:shadow-sm text-xs" data-testid={`renewal-row-${r.contract_number}`}>
                    <div onClick={() => nav(`/contract/${r.contract_id}`)} className="cursor-pointer">
                      <div className="font-mono text-[10px] text-slate-500">#{r.contract_number}</div>
                      <div className="font-semibold text-slate-900 truncate">{r.vendor}</div>
                      <div className="text-slate-500 truncate">{r.department}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-slate-500">{new Date(r.renewal_date).toLocaleDateString()}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${r.days_until < 0 ? "bg-red-100 text-red-700" : r.days_until <= 90 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                          {r.days_until < 0 ? `${-r.days_until}d over` : `${r.days_until}d`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-slate-500">{r.amount ? currency(r.amount) : "—"}</span>
                        {r.auto_renewal ? <span className="text-[9px] px-1 py-0.5 bg-red-50 text-red-700 rounded font-bold">AUTO-RENEW</span> : null}
                      </div>
                    </div>
                    {r.renewal_decision ? (
                      <div className="mt-2 text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-800 text-white inline-block">
                        Decided: {r.renewal_decision.replace('_',' ')}
                      </div>
                    ) : canDecide ? (
                      <div className="flex gap-1 mt-2">
                        <button onClick={(e) => { e.stopPropagation(); openDecide(r, "renew"); }} data-testid={`decide-renew-${r.contract_number}`}
                                className="flex-1 text-[10px] px-1 py-1 rounded bg-emerald-600 text-white flex items-center justify-center gap-0.5"><Check className="w-3 h-3"/>Renew</button>
                        <button onClick={(e) => { e.stopPropagation(); openDecide(r, "renegotiate"); }} data-testid={`decide-renegotiate-${r.contract_number}`}
                                className="flex-1 text-[10px] px-1 py-1 rounded bg-amber-500 text-white flex items-center justify-center gap-0.5"><Zap className="w-3 h-3"/>Re-neg</button>
                        <button onClick={(e) => { e.stopPropagation(); openDecide(r, "let_lapse"); }} data-testid={`decide-lapse-${r.contract_number}`}
                                className="flex-1 text-[10px] px-1 py-1 rounded bg-slate-500 text-white flex items-center justify-center gap-0.5"><XCircle className="w-3 h-3"/>Lapse</button>
                      </div>
                    ) : null}
                    <button
                      onClick={(e) => { e.stopPropagation(); setNotesFor(r); }}
                      data-testid={`renewal-notes-open-${r.contract_number}`}
                      className="mt-2 w-full text-[10px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 flex items-center justify-center gap-1 text-slate-700 font-semibold">
                      <MessageSquare className="w-3 h-3"/>
                      {r.notes_count > 0 ? `Notes · ${r.notes_count}` : "Add note"}
                    </button>
                    {r.latest_note ? (
                      <div className="mt-1 text-[10px] text-slate-500 truncate italic" title={r.latest_note.body} data-testid={`renewal-latest-note-${r.contract_number}`}>
                        {r.latest_note.author_name}: “{r.latest_note.body}”
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <RenewalNotesModal
        open={!!notesFor}
        contract={notesFor}
        onClose={() => setNotesFor(null)}
        onChanged={load}
      />
      <RenewalDecisionModal
        open={!!decideFor}
        contract={decideFor?.contract}
        decision={decideFor?.decision}
        onClose={() => setDecideFor(null)}
        onDone={load}
      />
      <AttorneyDigestPreviewModal
        open={digestOpen}
        onClose={() => setDigestOpen(false)}
        onSent={load}
      />
    </div>
  );
}
